import { useCallback, useEffect, useMemo } from "react";
import { usePrevious } from "react-use";
import { throttle, uniqWith } from "lodash";
import mapboxgl, { FeatureIdentifier } from "mapbox-gl";

import { getCurrentProjectHighlights } from "fond/project";
import { openFeatureSelectionPopup, selectFeature, unselectFeature } from "fond/project/redux";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { clickEventBBox } from "fond/utils/mapbox";

/**
 * A hook that adds click events to the passed map to handle feature selection & highlighting.
 */
export const useFeatureHandler = (map?: mapboxgl.Map): void => {
  const dispatch = useAppDispatch();
  const highlightedFeatures: FeatureIdentifier[] | undefined = useAppSelector((state) => getCurrentProjectHighlights(state.project));
  const previousHighlights: FeatureIdentifier[] | undefined = usePrevious(highlightedFeatures || []);

  /**
   * Handles setting the features states
   * For example a featureState { isSelected: true } will hightlight the features
   * @param object The features & state value to be changed.
   */
  const setFeatureState = useCallback(
    ({ features, state }: { features: FeatureIdentifier[] | mapboxgl.MapboxGeoJSONFeature[]; state: Record<string, unknown> }) => {
      features.forEach((feature) => {
        map?.setFeatureState(
          {
            source: feature.source,
            sourceLayer: feature.sourceLayer,
            id: feature.id,
          },
          state
        );
      });
    },
    [map]
  );

  /**
   * Handles the monitoring of the highlighted features
   */
  useEffect(() => {
    // Unhighlighted the previous highlighted features
    if (previousHighlights?.length) {
      setFeatureState({ features: previousHighlights, state: { isSelected: false } });
    }

    // Highlight the newly selected features
    if (highlightedFeatures?.length) {
      setFeatureState({ features: highlightedFeatures, state: { isSelected: true } });
    }
  }, [highlightedFeatures, previousHighlights, setFeatureState]);

  /**
   * Handles selecting or deselecting a feature via redux,
   * @param feature The feature to be selected, if undefined the current selection will be deselected.
   * @param lngLat
   */
  const handleOnSelectFeature = useCallback(
    (feature: mapboxgl.MapboxGeoJSONFeature | undefined, lngLat: mapboxgl.LngLatLike) => {
      // We currently do not support the boundary feature being selected
      if (feature && feature.properties?.type !== "boundary") {
        const sourceVersionId = (feature.layer as { metadata?: { versionId?: string } })?.metadata?.versionId;
        dispatch(selectFeature(feature, lngLat, false, sourceVersionId));
      } else {
        dispatch(unselectFeature());
      }
    },
    [dispatch]
  );

  /**
   * Given a Mapbox mouse event, return selectable FOND features (as opposed to base
   * Mapbox features like landmarks, which we don't let users interact with)
   * within 5 pixels of the event.
   *
   * Returns both boundary/subarea features and overlay features, excluding the
   * outer boundary (type === "boundary") which is not selectable.
   * @param event
   */
  const getFeatures = useCallback(
    (event: mapboxgl.MapMouseEvent) => {
      const queryBbox = clickEventBBox(event);
      const allFeatures = map?.queryRenderedFeatures(queryBbox) ?? [];

      // Boundary/subarea features, excluding the outer boundary which is not selectable
      const boundaryFeatures = uniqWith(
        allFeatures.filter((feature) => feature.properties?.boundaryId != null && feature.properties?.type !== "boundary"),
        (valueA, valueB) => valueA.id === valueB.id
      );

      // Overlay features (from overlay sources rendered by OverlayMapContent)
      const overlayFeatures = uniqWith(
        allFeatures.filter((feature) => feature.source?.startsWith("overlay-")),
        (valueA, valueB) => valueA.id === valueB.id
      );

      return [...boundaryFeatures, ...overlayFeatures];
    },
    [map]
  );

  /**
   * Handles the click event for the map.
   * If multiple selectable features are found, opens a popup for disambiguation.
   */
  const handleOnMapClick = useCallback(
    (event: mapboxgl.MapMouseEvent) => {
      const features = getFeatures(event);
      if (features.length > 1) {
        dispatch(openFeatureSelectionPopup(features, event.lngLat));
      } else {
        handleOnSelectFeature(features?.[0], event.lngLat);
      }
    },
    [dispatch, getFeatures, handleOnSelectFeature]
  );

  /**
   * Handles the mouse move event for the map.
   * Throttled to limit queryRenderedFeatures calls for cursor updates.
   */
  const handleOnMouseMove = useMemo(
    () =>
      throttle((event: mapboxgl.MapMouseEvent) => {
        if (map) {
          const newCursor = getFeatures(event).length > 0 ? "pointer" : "";
          if (map.getCanvas().style.cursor !== newCursor) {
            // eslint-disable-next-line no-param-reassign
            map.getCanvas().style.cursor = newCursor;
          }
        }
      }, 100),
    [map, getFeatures]
  );

  useEffect(() => {
    map?.on("click", handleOnMapClick);
    map?.on("mousemove", handleOnMouseMove);
    return () => {
      map?.off("click", handleOnMapClick);
      map?.off("mousemove", handleOnMouseMove);
      handleOnMouseMove.cancel();
    };
  }, [handleOnMapClick, handleOnMouseMove, map]);
};
